import config from '../../config';

const ANALYTICS_URL = `${config.baseURL}/analytics/events`;
const SESSION_KEY = '_analytics_sid';

// Get or create a session ID stored in localStorage
function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

/**
 * Track an analytics event. Fire-and-forget — never blocks UI or throws.
 * @param {string} eventName - e.g. "page_view", "product_click", "search"
 * @param {object} payload - event-specific data, e.g. { pharmacode: "123" }
 */
export function trackEvent(eventName, payload = {}) {
  try {
    const body = JSON.stringify({
      eventName,
      timestamp: new Date().toISOString(),
      payload,
      context: {
        path: window.location.pathname,
        referrer: document.referrer,
      },
    });

    // Use navigator.sendBeacon for reliability (survives page unload)
    // Falls back to fetch if sendBeacon unavailable
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ANALYTICS_URL, blob);
    } else {
      fetch(ANALYTICS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'include',
      }).catch(() => {});
    }
  } catch {
    // Silent fail — analytics should never break the app
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Product click attribution
//
// Every product-page open is reported with the list the visitor came from
// (all products, bestsellers, discounts, search …) so the admin can see which
// surfaces actually drive traffic. Two signals, in priority order:
//
//   1. An explicit tag set right before navigating (setClickSource). Needed
//      where several lists share one URL — the home page rails all live on
//      "/", so the route alone can't tell bestsellers from offers.
//   2. Otherwise, inferred from the previous in-app route
//      (/products?view=discount → "discounts", /categories/… → "category").
// ──────────────────────────────────────────────────────────────────────────

// An explicit tag is only trusted briefly, so a tag set before a navigation
// that never happened can't leak onto a later, unrelated product open.
const EXPLICIT_SOURCE_TTL_MS = 5000;
let explicitSource = null;
// Recent in-app routes (pathname + search), newest last.
const routeHistory = [];

/** Tag the next product open with where it was clicked. */
export function setClickSource(source) {
  explicitSource = { source, at: Date.now() };
}

/** Called on every route change (see usePageTracking). */
export function recordRoute(pathname, search = '') {
  const last = routeHistory[routeHistory.length - 1];
  if (last && last.pathname === pathname && last.search === search) return;
  routeHistory.push({ pathname, search });
  if (routeHistory.length > 10) routeHistory.shift();
}

function sourceFromRoute(route) {
  const { pathname, search } = route;
  const params = new URLSearchParams(search);
  if (pathname === '/products') {
    const view = params.get('view');
    if (view === 'bestseller') return 'bestsellers';
    if (view === 'discount') return 'discounts';
    if (params.get('campaign')) return 'campaign';
    if (params.get('search') || params.get('symptom')) return 'search';
    if (view === 'generic' || view === 'field-search') return 'related';
    return 'all_products';
  }
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/categories')) return 'category';
  if (pathname.startsWith('/product/')) return 'related';
  if (pathname === '/cartpage' || pathname === '/checkout') return 'cart';
  if (pathname === '/userfavoritespage') return 'favorites';
  if (pathname === '/orders' || pathname === '/orderdetailspage') return 'orders';
  return 'other';
}

/**
 * Resolve (and consume) the source for the product page at `currentPath`.
 * Order-independent: it ignores the product route itself whether or not it
 * has been recorded yet.
 */
export function resolveClickSource(currentPath) {
  const tag = explicitSource;
  explicitSource = null;
  if (tag && Date.now() - tag.at < EXPLICIT_SOURCE_TTL_MS) return tag.source;

  for (let i = routeHistory.length - 1; i >= 0; i--) {
    if (routeHistory[i].pathname !== currentPath) return sourceFromRoute(routeHistory[i]);
  }

  // Landed straight on the product page: from another site, or typed/bookmarked.
  try {
    if (document.referrer && new URL(document.referrer).host !== window.location.host) return 'external';
  } catch {
    // Malformed referrer — treat as direct.
  }
  return 'direct';
}
